// src/components/RequirePlan.jsx
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCompany } from "../context/CompanyContext";
import "./RequirePlan.css";

/**
 * ✅ Updated to NEW plans:
 * free -> starter -> sme -> business
 * (replacing "pro" with "sme")
 */
const PLAN_RANK = {
  free: 0,
  starter: 1,
  sme: 2,
  business: 3,
};

function normalizePlan(p) {
  const v = String(p || "free")
    .toLowerCase()
    .trim();

  // Backward-compat mappings (keeps existing data working)
  if (v === "basic") return "starter";
  if (v === "premium") return "sme";
  if (v === "pro") return "sme"; // ✅ old plan maps to SME now

  if (["free", "starter", "sme", "business"].includes(v)) return v;
  return "free";
}

/**
 * RequirePlan
 *
 * Props:
 * - min: minimum plan required (default: "free")
 * - featureName: label used in upgrade UI
 * - mode:
 *    - "hide"    → feature is NOT rendered at all (default)
 *    - "upgrade" → show upgrade CTA
 */
export default function RequirePlan({
  min = "free",
  featureName = "this feature",
  mode = "hide",
  children,
}) {
  const { plan } = useCompany();
  const location = useLocation();

  const current = useMemo(() => normalizePlan(plan), [plan]);
  const needed = useMemo(() => normalizePlan(min), [min]);

  const allowed = (PLAN_RANK[current] ?? 0) >= (PLAN_RANK[needed] ?? 0);

  // ✅ Allowed → render feature
  if (allowed) return children;

  // ❌ Not allowed + hide mode → render nothing
  if (mode === "hide") return null;

  // ❌ Not allowed + upgrade mode → show upgrade UI
  return (
    <div className="kp-upgrade-wrap">
      <div className="kp-upgrade-card">
        <h2>Upgrade to unlock</h2>
        <p>
          Your current plan is <strong>{current.toUpperCase()}</strong>.
          <br />
          <strong>{featureName}</strong> requires{" "}
          <strong>{needed.toUpperCase()}</strong> or higher.
        </p>

        <div className="kp-upgrade-actions">
          <Link to="/billings" className="btn-primary">
            Upgrade plan
          </Link>
          <Link to="/dashboard" className="btn-ghost">
            Back to Dashboard
          </Link>
        </div>

        <p className="kp-upgrade-note">
          You tried to access: <code>{location.pathname}</code>
        </p>
      </div>
    </div>
  );
}
